
import {
  computed, defineComponent, inject, PropType, Ref, ref, SetupContext, toRef, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Tooltip from 'primevue/tooltip';
import Button from 'primevue/button';
import Dropdown from 'primevue/dropdown';
import Dialog from 'primevue/dialog';
import BaseDropdown from '@/components/fields/partials/BaseDropdown.vue';
import tooltip from '@/components/automated-scenarios/forms/filtres/segments/tooltips';
import {
  ItemList,
  NumberRange,
  ButtonGroup,
  InputList,
  MultiDropdown,
  EditAutocomplete,
  InputNumber,
  PeriodSelector,
  Input,
  Location,
} from '@/components/flows/operators/shared';
import type { FilterField, FilterSegment } from '@/types/flow-types';
import { ActiveSegment, getAvailableSegmentsForFlow, FlowDependencyContext } from '@/composables/flows/useFlowSegments';
import type { OperatorInsertContext } from '@/components/flows/operators/registry';
import { useStore } from '@/store';
import { asInt, showToastError } from '@/helpers';
import FieldErrors from '@/components/fields/partials/FieldErrors.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { TypeCampaignEnum } from '@/types';

import { List } from '@/composables/GraphQL';
import { UserState } from '@/composables/User';
import { myListsState } from '@/composables/mes-listes/ListsManage';
import { OperatorType, ShopsLists } from '@/types/generated-types/graphql';

export default defineComponent({
  name: 'FilterSegmentForm',

  components: {
    SpmButton,
    FieldErrors,
    BaseDropdown,
    Button,
    Dialog,
    ItemList,
    NumberRange,
    ButtonGroup,
    InputNumber,
    Input,
    Location,
    PeriodSelector,
    InputList,
    Dropdown,
    MultiDropdown,
    EditAutocomplete,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    segments: {
      type: Array as PropType<FilterSegment<unknown>[]>,
      required: true,
    },

    activeSegments: {
      type: Array as PropType<ActiveSegment[]>,
      required: true,
    },

    items: {
      type: Object,
      required: true,
    },

    segmentLabel: {
      type: String,
      required: false,
      default: 'automatedScenarios.fields.addSegment',
    },

    placeholderSelectSegments: {
      type: String,
      required: false,
      default: 'automatedScenarios.fields.segments.chooseSegment',
    },

    scenarioType: {
      type: String as PropType<TypeCampaignEnum>,
      required: false,
      default: TypeCampaignEnum.AUTOMATION,
    },

    currentStepId: {
      type: String,
      required: false,
      default: null,
    },

  },

  emits: ['add:segment', 'remove:segment'],

  setup(props: {
    segments: FilterSegment<unknown>[];
    activeSegments: ActiveSegment[];
    items: any;
    scenarioType: TypeCampaignEnum;
    currentStepId: string | null;
  }, { emit }: SetupContext) {
    const { t } = useI18n();
    const store = useStore();
    const formValidation = ref<any>({});
    const componentFieldErrorsKey = ref(0);

    // Get flow from store for dependency checking
    const activeFlow = computed(() => store.getters['flowEditor/getActiveFlow']);

    // Get insert context from parent (for new operators being added)
    const insertContext = inject<Ref<OperatorInsertContext | null>>('insertContext', ref(null));

    const flowContext = computed<FlowDependencyContext>(() => {
      const flow = activeFlow.value?.flowData ?? null;

      // If we have a currentStepId (edit mode), use it
      if (props.currentStepId) {
        return {
          flow,
          currentStepId: props.currentStepId,
        };
      }

      // For new operators, use insert context to get ancestor types
      if (insertContext?.value) {
        return {
          flow,
          currentStepId: null,
          directParentTypes: insertContext.value.parentOperatorId ? [insertContext.value.parentOperatorId] : [],
          allAncestorTypes: insertContext.value.allAncestorTypes ?? [],
        };
      }

      return {
        flow,
        currentStepId: null,
      };
    });

    const segments = toRef(props, 'segments');
    const selectedValue = ref('');
    const selectedSegment: Ref<FilterSegment<unknown>|undefined> = ref();
    const selectedSegmentNumber: Ref<number|null> = ref(null);
    const editableFields = computed(() => selectedSegment.value?.fields.filter((field: FilterField) => !field.hidden));

    // Component mapping for dynamic component resolution
    const componentMap: Record<string, any> = {
      ItemList,
      NumberRange,
      ButtonGroup,
      InputNumber,
      Input,
      Location,
      PeriodSelector,
      InputList,
      MultiDropdown,
      EditAutocomplete,
    };

    const resolveComponent = (componentName: string) => componentMap[componentName] || componentName;

    const newListId = ref();
    const showModal = ref(false);

    const options = computed(() => getAvailableSegmentsForFlow<unknown>(
      props.scenarioType,
      segments.value,
      props.activeSegments.map((activeSegment) => activeSegment.name),
      flowContext.value,
    )
      .filter((segment: FilterSegment<unknown>) => ((segment.multiple
        || !props.activeSegments.reduce((acc: string[], current: ActiveSegment) => ([...acc, current.name]), []).includes(segment.id))))
      .map((segment: FilterSegment<unknown>) => segment.option));

    const resetAll = () => {
      showModal.value = false;
      selectedSegment.value = undefined;
      selectedValue.value = '';
      selectedSegmentNumber.value = null;
    };

    const onModalHide = () => {
      resetAll();
    };

    const hasActiveSegment = () => props.activeSegments.length > 0;

    const handleRemoveSegment = (segmentName: string, number: number) => {
      emit('remove:segment', { segmentName, number });
    };

    const handleSaveSegment = async () => {
      formValidation.value = {};
      componentFieldErrorsKey.value += 1;
      if (selectedSegment?.value?.validate) {
        const data = selectedSegment.value?.fields.reduce((acc: {[key: number]: string}, cur: any) => {
          acc[cur.id] = cur.data;
          return acc;
        }, {});
        const validation = await selectedSegment.value.validate(data);
        if (!validation.success) {
          formValidation.value = validation.validate;
          componentFieldErrorsKey.value += 1;
          await showToastError(t('errorMessages.FORM_ERROR'));
          return;
        }
      }

      showModal.value = false;
      if (selectedSegmentNumber.value !== null && selectedSegment.value) {
        handleRemoveSegment(selectedSegment.value.id, selectedSegmentNumber.value);
      }
      if (selectedSegment.value) {
        emit('add:segment', selectedSegment.value);
      }
      selectedSegment.value = undefined;
      selectedValue.value = '';
      selectedSegmentNumber.value = null;
    };

    const loadSegment = (segment: string|any) => {
      formValidation.value = {};
      componentFieldErrorsKey.value += 1;

      selectedValue.value = typeof (segment) === 'string' ? segment : segment.value;

      selectedSegment.value = segments.value.filter((current: FilterSegment<unknown>) => current.option.value === selectedValue.value)[0];

      if (editableFields.value?.length === 0) {
        handleSaveSegment();
      } else {
        showModal.value = true;
      }
    };

    const editSegment = (segmentName: string, number?: number) => {
      selectedSegmentNumber.value = number !== undefined ? number : null;
      loadSegment(segmentName);

      selectedSegment.value?.fields.forEach((field: FilterField) => {
        field.data = props.items[segmentName][field.id];

        if (number !== undefined) {
          if (field.component === 'InputList') {
            field.data = props.items[segmentName][field.id];
          } else {
            field.data = props.items[segmentName][field.id][number];
          }
        }

        if (field.format) {
          field.data = field.format(field.data);
        }
      });
    };

    const showTooltip = (segmentName: string, number: number) => {
      const segment = segments.value.filter((current: FilterSegment<unknown>) => current.option.value === segmentName)[0];
      return tooltip(segment, props.items[segmentName], number);
    };

    const fetchLists = async () => {
      const { items, err } = await List<ShopsLists>({
        name: 'ShopsLists',
        settings: {
          filter: [
            {
              field: 'id_shop',
              operator: OperatorType.Equals,
              value: UserState.activeShop ? UserState.activeShop.id : 0,
            },
          ],
          order: [
            { field: 'id_shop_list', type: 'ASC' },
          ],
          limit: 0,
          offset: 0,
        },
        fields: ['id_shop_list', 'name'],
      });

      if (err === '') {
        return items.map((item) => ({
          name: item.name,
          code: item.id_shop_list,
        }));
      }

      return [];
    };

    watch(() => myListsState.refreshComponent, async () => {
      if (myListsState.refreshComponent !== 0 && selectedSegment.value && selectedSegment.value.id === 'filtermemberoflist') {
        newListId.value = myListsState.refreshComponent;
        const newLists = await fetchLists();

        const matchingCurrentLists = newLists.find((item: any) => item.code === asInt(newListId.value));
        if (matchingCurrentLists) {
          selectedSegment.value.fields[0].props.options = newLists.map((list) => ({ value: list.code, label: list.name }));
        }

        myListsState.refreshComponent = 0;
      }
    });

    return {
      t,
      formValidation,
      componentFieldErrorsKey,
      selectedValue,
      selectedSegment,
      options,
      editableFields,
      newListId,
      showModal,
      resolveComponent,

      showTooltip,
      loadSegment,
      editSegment,
      handleSaveSegment,
      handleRemoveSegment,
      hasActiveSegment,
      resetAll,
      onModalHide,
    };
  },

});
